import { useEffect, useState } from 'react';
import { useLocation, Route, Switch, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/system';
import { NavTabs } from '@components/NavTabs';
import { ControlledToaster } from '@components/Toaster';
import { ACCOUNT_SETTINGS_TABS } from '@constants/tabs';
import { useTranslation } from '@hooks';
import { ROLE } from '@constants/user';
import { RoutePath } from '@constants/routes';
import { GeneralSettings } from './GeneralSettings';
import Companies from './Companies';
import UserManagement from './UserManagement';
import Integrations from './Integrations';
import Profile from './Profile';
import Channels from './Channels';
import Brands from './Brands';

const PageWrapper = styled('div')({
    '@media (max-width: 1024px)': {
        height: `calc(var(--100vh) - 108px)`,
        padding: '16px 16px 0',
        overflowY: 'auto',
    },
});

const AccountSettingsPage = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();

    const [availableTabs, setAvailableTabs] = useState([]);
    const [openAlert, setOpenAlert] = useState(false);
    const [currentTab, setCurrentTab] = useState(pathname);

    const user = useSelector(state => state.auth.user);
    const account = useSelector(state => state.account?.account);

    const isSuperAdmin = user?.role === ROLE.SUPER_ADMIN;
    const isAdmin = user?.role === ROLE.ADMIN;
    const productsMaxNumberExceeded =
        account?.companiesMaxCount < account?.companies?.length || account?.usersMaxCount < account?.users?.length;
    const billingExpired = account && account?.billingDiffInSeconds < 0;

    useEffect(() => {
        setCurrentTab(pathname);
    }, [pathname]);

    useEffect(() => {
        if (productsMaxNumberExceeded || billingExpired) {
            setOpenAlert(true);
        }
    }, [productsMaxNumberExceeded, billingExpired]);

    useEffect(() => {
        let translatedTabs = ACCOUNT_SETTINGS_TABS.map(tab => {
            const isDisabled =
                (tab.value === RoutePath.AccountSettingsIntegrations ||
                    tab.value === RoutePath.AccountSettingsChannels) &&
                (productsMaxNumberExceeded || billingExpired);
            return {
                ...tab,
                label: t(tab.label),
                disabledTooltip: isDisabled ? t('common.subscriptionExpiredMsg') : null,
                disabled: isDisabled,
            };
        });
        if (!isSuperAdmin) {
            translatedTabs = translatedTabs.filter(tab => {
                return (
                    tab.value !== RoutePath.AccountSettingsGeneral &&
                    tab.value !== RoutePath.AccountSettingsIntegrations &&
                    tab.value !== RoutePath.AccountSettingsBrands &&
                    tab.value !== RoutePath.AccountSettingsChannels
                );
            });
        }
        if (!isSuperAdmin && !isAdmin) {
            translatedTabs = translatedTabs.filter(tab => tab.value === RoutePath.AccountSettingsProfile);
        }
        setAvailableTabs(translatedTabs);
    }, [isSuperAdmin, isAdmin, account]);

    const getAvailableTabIndex = () => {
        if (isSuperAdmin) {
            return {
                [RoutePath.AccountSettingsProfile]: 0,
                [RoutePath.AccountSettingsGeneral]: 1,
                [RoutePath.AccountSettingsCompanies]: 2,
                [RoutePath.AccountSettingsBrands]: 3,
                [RoutePath.AccountSettingsChannels]: 4,
                [RoutePath.AccountSettingsUsers]: 5,
                [RoutePath.AccountSettingsIntegrations]: 6,
            };
        } else if (isSuperAdmin || isAdmin) {
            return {
                [RoutePath.AccountSettingsProfile]: 0,
                [RoutePath.AccountSettingsCompanies]: 1,
                [RoutePath.AccountSettingsUsers]: 2,
            };
        } else {
            return {
                [RoutePath.AccountSettingsProfile]: 0,
            };
        }
    };

    return (
        <PageWrapper>
            <NavTabs
                tabLinks={availableTabs}
                onTabClick={selectedTab => setCurrentTab(selectedTab)}
                currentTabIndex={getAvailableTabIndex()[currentTab]}
            />
            <Switch>
                <Route path={RoutePath.AccountSettingsProfile} component={Profile} />
                <Route path={RoutePath.AccountSettingsGeneral} component={GeneralSettings} />
                <Route path={RoutePath.AccountSettingsCompanies} component={Companies} />
                <Route path={RoutePath.AccountSettingsBrands} component={Brands} />
                <Route path={RoutePath.AccountSettingsChannels} component={Channels} />
                <Route path={RoutePath.AccountSettingsUsers} component={UserManagement} />
                <Route path={RoutePath.AccountSettingsIntegrations} component={Integrations} />
                <Redirect from={RoutePath.AccountSettings} to={RoutePath.AccountSettingsProfile} />
            </Switch>
            <ControlledToaster
                autoHideDuration={null}
                text={t('common.subscriptionExpiredMsg')}
                type="error"
                open={openAlert}
                setOpen={setOpenAlert}
            />
        </PageWrapper>
    );
};

export default AccountSettingsPage;
